export type WhitelistRulePayload = {
  setA?: string[];
  setB?: string[];
  regulatory_body?: string[];
  justification?: string;
};

export type WhitelistNewEntry = {
  key: string;
  regulatory_body: string[];
  justification: string;
};

/** Backend does not persist catalog-rule removals yet; flip when POST /project/whitelist-rules replaces whitelist_rules. */
export const WHITELIST_PROJECT_RULE_REMOVAL_ENABLED = false;

export const extractSelectedWhitelistRuleKeys = (whitelistRules: unknown): string[] => {
  if (whitelistRules == null) return [];

  if (Array.isArray(whitelistRules)) {
    const keys: string[] = [];
    whitelistRules.forEach((ruleObj: unknown) => {
      if (typeof ruleObj === 'string') {
        keys.push(ruleObj);
      } else if (ruleObj && typeof ruleObj === 'object') {
        keys.push(...Object.keys(ruleObj as Record<string, unknown>));
      }
    });
    return keys;
  }

  if (typeof whitelistRules === 'object') {
    return Object.keys(whitelistRules as Record<string, unknown>);
  }

  return [];
};

export const buildWhitelistRulesPayload = (
  whitelistRules: unknown,
  selectedKeys: string[]
): Record<string, WhitelistRulePayload> =>
  selectedKeys.reduce<Record<string, WhitelistRulePayload>>((acc, ruleKey) => {
    let ruleData: WhitelistRulePayload | null = null;

    if (Array.isArray(whitelistRules)) {
      const matchingEntry = whitelistRules.find(
        (entry: unknown) => entry && typeof entry === 'object' && ruleKey in (entry as Record<string, unknown>)
      ) as Record<string, WhitelistRulePayload> | undefined;
      if (matchingEntry?.[ruleKey]) {
        ruleData = matchingEntry[ruleKey];
      }
    } else if (whitelistRules && typeof whitelistRules === 'object' && ruleKey in whitelistRules) {
      ruleData = (whitelistRules as Record<string, WhitelistRulePayload>)[ruleKey];
    }

    if (ruleData) {
      acc[ruleKey] = ruleData;
    }
    return acc;
  }, {});

export const resolveProjectWhitelistState = (
  projectDetail: { whitelist_rules?: unknown } | null | undefined,
  selectedProject: { whitelist_rules?: unknown } | null | undefined
): { selectedRules: string[]; newEntries: WhitelistNewEntry[] } => ({
  selectedRules: extractSelectedWhitelistRuleKeys(projectDetail?.whitelist_rules ?? selectedProject?.whitelist_rules),
  newEntries: []
});

const areRuleSelectionsEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false;
  const leftSet = new Set(left);
  return right.every(key => leftSet.has(key));
};

/** True when server-stored whitelist keys match the keys we just saved. */
export const whitelistRuleKeysMatchSavedPayload = (serverRules: unknown, savedKeys: string[]): boolean =>
  areRuleSelectionsEqual(extractSelectedWhitelistRuleKeys(serverRules), savedKeys);

const hasMeaningfulNewEntries = (entries: WhitelistNewEntry[]): boolean =>
  entries.some(
    entry => entry.key.trim().length > 0 || entry.regulatory_body.length > 0 || entry.justification.trim().length > 0
  );

/** True when whitelist UI differs from last saved project configuration. */
export const hasUnsavedWhitelistChanges = (
  projectDetail: { whitelist_rules?: unknown } | null | undefined,
  selectedProject: { whitelist_rules?: unknown } | null | undefined,
  selectedRules: string[],
  newEntries: WhitelistNewEntry[]
): boolean => {
  const serverState = resolveProjectWhitelistState(projectDetail, selectedProject);
  if (hasMeaningfulNewEntries(newEntries)) return true;
  return !areRuleSelectionsEqual(serverState.selectedRules, selectedRules);
};
